import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NgxPermissionsService } from 'ngx-permissions';
import { ConfirmationService, MessageService } from 'primeng/api';
import { lastValueFrom, map } from 'rxjs';
import {
  Category,
  CategorySummaryDTO,
  ProductCategoryCreateRequest,
  ProductCategoryUpdateRequest,
  ProductControllerService
} from 'src/app/admin-api';
import { Role, roleAsObject } from 'src/app/models';
import { AppDialogService } from 'src/app/services/dialog.service';
import { LoaderService } from 'src/app/services/loader.service';
import { CategoryUtil } from 'src/app/utils/category.util';

@Component({
  selector: 'app-product-category-form',
  templateUrl: './product-category-form.component.html',
  styleUrls: ['./product-category-form.component.scss']
})
export class ProductCategoryFormComponent implements OnInit {
  private productService: ProductControllerService = inject(
    ProductControllerService
  );
  private router: Router = inject(Router);
  private messageService: MessageService = inject(MessageService);
  private ngxPermissionsService: NgxPermissionsService = inject(
    NgxPermissionsService
  );
  private confirmationService = inject(ConfirmationService);

  @Input()
  model: Category | undefined;

  @Output()
  updated = new EventEmitter<Category>();

  @Input()
  children = false;

  @Input()
  categories: Array<CategorySummaryDTO> | undefined;

  form = new FormGroup({
    categoryId: new FormControl<number>(null),
    displayName: new FormControl<string>(null, Validators.required),
    externalId: new FormControl<number>(null, [
      Validators.required,
      Validators.min(1)
    ]),
    parentId: new FormControl(0, [Validators.required])
  });
  categoriesForDropdown: Array<CategorySummaryDTO> | undefined;
  categoriesList: Array<CategorySummaryDTO> | undefined;
  permission = false;
  acceptedPermissions = [
    roleAsObject(Role.Full_Administrator).enumValue,
    roleAsObject(Role.Admin_Allocation).enumValue,
    roleAsObject(Role.User_Allocation).enumValue
  ];
  subparentCategories: Array<Category> | undefined;

  async ngOnInit() {
    this.permission = await this.ngxPermissionsService.hasPermission(
      this.acceptedPermissions
    );
    this.categoriesList = this.categories?.reduce(
      (cats, c) => CategoryUtil.expandToList(c, null, cats),
      []
    );
    if (this.model?.categoryId) this.form.patchValue(this.model);
    if (!this.permission) this.form.disable();
    this.categoriesForDropdown = [
      {
        categoryId: 0,
        category: 'Sem categoria',
        externalId: 0,
        parentId: 0
      } as CategorySummaryDTO
    ].concat(
      this.categories.reduce(
        (cats: CategorySummaryDTO[], c) =>
          CategoryUtil.expandToList(c, this.model?.categoryId, cats),
        []
      )
    );
    if (this.last?.externalId && !this.model) {
      this.form.controls['externalId'].setValue(this.last.externalId + 1);
    }
  }

  async save(): Promise<void> {
    if (this.form?.valid) {
      LoaderService.showLoader();
      try {
        if (this.model?.categoryId) {
          this.model = await lastValueFrom(
            this.productService
              .updateProductCategory(
                this.form.value as ProductCategoryUpdateRequest
              )
              .pipe(map((data) => data.result))
          );
          this.updated.emit(this.model);
          this.messageService.add({
            severity: 'success',
            detail: 'Categoria atualizada com sucesso',
            summary: 'Sucesso'
          });
        } else {
          this.model = await lastValueFrom(
            this.productService
              .createProductCategory(
                this.form.value as ProductCategoryCreateRequest
              )
              .pipe(map((data) => data.result))
          );
          this.messageService.add({
            severity: 'success',
            detail: 'Categoria cadastrada com sucesso',
            summary: 'Sucesso'
          });
          this.router.navigate([
            'products/categories/' + this.model?.categoryId
          ]);
        }
      } catch (error: any) {
        AppDialogService.showErrorDialog(error);
      }
      LoaderService.showLoader(false);
    }
  }

  validateExternalId() {
    if (this.conflictCategory) {
      this.form.controls.externalId.setErrors({ exists: true });
    } else {
      this.form.controls.externalId.setErrors(null);
      this.form.controls.externalId.updateValueAndValidity();
    }
  }

  get parent(): CategorySummaryDTO {
    return this.categoriesForDropdown?.find(
      (c) => c.categoryId === this.form?.value?.parentId
    );
  }

  get last(): Category {
    const cats = [
      ...(this.categoriesList?.filter(
        (c) => c.parentId === this.form?.value.parentId
      ) || [])
    ];
    cats.sort(
      (c1, c2) => (c2.externalId as number) - (c1.externalId as number)
    );
    return cats[0] as Category;
  }

  get conflictCategory() {
    return this.categoriesList?.find(
      (c) =>
        c.externalIdString === this.externalId &&
        (!this.model?.categoryId || c.categoryId !== this.model.categoryId)
    );
  }

  get formGroup() {
    return new FormGroup({
      categoryId: new FormControl(this.model?.categoryId),
      displayName: new FormControl(
        { value: this.model?.displayName, disabled: !this.permission },
        Validators.required
      ),
      externalId: new FormControl(this.model?.externalId, [
        Validators.required,
        Validators.min(1)
      ]),
      parentId: new FormControl(this.model?.parentId || 0, [
        Validators.required
      ])
    });
  }

  get externalId() {
    return `${
      this.parent?.externalIdString ? this.parent.externalIdString + '.' : ''
    }${this.form.value.externalId}`;
  }
}
